import { KiteIcon } from '@kite/react-kite';
import React, { Children, TouchEvent, useState } from 'react';
import './ImageCarousel.scss';
export interface CarouselInputs {
    id: string;
    title?: string;
    body?: string;
    background?: `dark-blue` | `light-blue` | `white`;
    arrows?: `large` | `small`;
    fullWidth?: boolean;
    children?: JSX.Element[];
    noPadding?: boolean;
}

export const ImageCarousel = ({
    inputs,
    children,
}: {
    inputs: CarouselInputs;
    children;
}) => {
    if (!inputs) return;

    const items = !inputs.children
        ? Children.toArray(children)
        : Children.toArray(inputs.children);

    const [currentSlide, setCurrentSlide] = useState(0);
    const [lastSlide, setLastSlide] = useState(0);
    const [touchPx, setTouchPx] = useState(0);
    const [lastTouchX, setLastTouchX] = useState<number>(undefined);

    const handleSlideChange = (direction: 1 | -1) => {
        let nextSlide = currentSlide + direction;
        if (nextSlide < 0) nextSlide = items.length - 1;
        if (nextSlide > items.length - 1) nextSlide = 0;
        setLastSlide(currentSlide);
        setCurrentSlide(nextSlide);
    };

    const touchEnd = () => {
        if (touchPx < -200) {
            handleSlideChange(-1);
        } else if (touchPx > 200) {
            handleSlideChange(1);
        }
        setTouchPx(0);
        setLastTouchX(undefined);
    };

    const touchMove = (evt: TouchEvent) => {
        if (!evt.touches[0]) return;
        const x = evt.touches[0].clientX;
        if (lastTouchX === undefined) {
            setLastTouchX(x);
        } else {
            const delta = lastTouchX - x;
            setTouchPx(delta);
        }
    };

    const touchCancel = () => {
        setTouchPx(0);
        setLastTouchX(undefined);
    };

    return (
        <>
            <h2 className="typestack-title-3 kite-container">{inputs.title}</h2>
            <div
                className="typestack-body-2 kite-container link-style"
                dangerouslySetInnerHTML={{ __html: inputs.body }}
            ></div>
            <section
                className={`${
                    inputs.fullWidth ? `` : `kite-container`
                } paragraph__field_carousel ${inputs.background} arrows-${
                    inputs.arrows
                }`}
            >
                <div
                    className={`wrapper ${
                        inputs.fullWidth ? `kite-container` : ``
                    }`}
                >
                    <button
                        onClick={() => {
                            handleSlideChange(-1);
                        }}
                        aria-label="View previous slide"
                        role="presentation"
                    >
                        <KiteIcon
                            icon={`${
                                inputs.arrows === `large`
                                    ? `ki-chevron-left`
                                    : `ki-chevron-left-circle`
                            }`}
                            size="2rem"
                        />
                    </button>
                    <ul
                        onTouchCancel={touchCancel}
                        onTouchEnd={touchEnd}
                        onTouchMove={touchMove}
                        onTouchStart={touchMove}
                        className={inputs.noPadding ? `no-pad` : undefined}
                    >
                        {Children.map(items, (item, idx) => {
                            let isAhead = idx > currentSlide;
                            let isAdjacent =
                                idx === currentSlide - 1 ||
                                idx === currentSlide + 1;
                            if (
                                idx === items.length - 1 &&
                                currentSlide === 0
                            ) {
                                isAhead = false;
                                isAdjacent = true;
                            }
                            if (
                                idx === 0 &&
                                currentSlide === items.length - 1
                            ) {
                                isAhead = true;
                                isAdjacent = true;
                            }

                            const isCurrent = idx === currentSlide;

                            const style = {
                                transform: `translateX(calc(${
                                    -100 * idx
                                }% - ${touchPx}px))`,
                                zIndex: 4,
                            };

                            if (!isCurrent) {
                                let zIndex = 1;
                                if (idx === lastSlide) {
                                    zIndex = 3;
                                } else if (isAdjacent) {
                                    zIndex = 2;
                                }

                                if (isAhead) {
                                    style.transform = `translateX(calc(${
                                        -100 * idx + 150
                                    }% - ${touchPx}px))`;
                                    style.zIndex = zIndex;
                                } else {
                                    style.transform = `translateX(calc(${
                                        -100 * idx - 150
                                    }% - ${touchPx}px))`;
                                    style.zIndex = zIndex;
                                }
                            }

                            return (
                                <li
                                    key={idx}
                                    style={style}
                                    className={
                                        (idx === currentSlide ||
                                            idx === lastSlide) &&
                                        touchPx === 0
                                            ? `should-animate`
                                            : ``
                                    }
                                >
                                    {item}
                                </li>
                            );
                        })}
                    </ul>
                    <button
                        onClick={() => {
                            handleSlideChange(1);
                        }}
                        aria-label="View next slide"
                        role="presentation"
                    >
                        <KiteIcon
                            icon={`${
                                inputs.arrows === `large`
                                    ? `ki-chevron-right`
                                    : `ki-chevron-right-circle`
                            }`}
                            size="2rem"
                        />
                    </button>
                </div>
            </section>
        </>
    );
};
